import type {
	ItemOrigin,
	MedJobErrorReason,
	MedJobType,
	MilestoneType,
	TranscriptClass,
	TranscriptOrigin,
} from "@/api/db/connection";
import type { MedicoPipelineItem } from "@/api/db/medico";
import type { MedicoErrorCode } from "@/api/router/medico";
import type { RedFlagRule } from "@/api/medico/rules";
import type { ProntuarioSection } from "@/api/medico/taxonomy";
import { actionError } from "@/lib/error-code";
import type { ChipTone, ChipToneOrNeutral } from "@/lib/labels/shared";

const transcriptClassLabel: Record<TranscriptClass, string> = {
	first: "1ª consulta",
	return: "Retorno",
	dictation: "Ditado da Dra.",
	not_consultation: "Não é consulta",
};

const transcriptClassTone: Record<TranscriptClass, ChipTone> = {
	first: "petrol",
	return: "teal",
	dictation: "amber",
	not_consultation: "coral",
};

const transcriptOriginLabel: Record<TranscriptOrigin, string> = {
	fireflies: "Fireflies",
	avulsa: "avulsa",
};

const transcriptOriginTone: Record<TranscriptOrigin, ChipToneOrNeutral> = {
	fireflies: "teal",
	avulsa: "neutral",
};

const jobTypeLabel: Record<MedJobType, string> = {
	extraction: "extração de prontuário",
	manual: "manual do paciente",
	exam: "leitura de exame",
	term_template: "geração de template de termo",
};

const jobErrorReasonLabel: Record<MedJobErrorReason, string> = {
	interrupted: "Interrompido por reinício do servidor",
	timeout: "Encerrado por exceder o tempo limite",
	failed: "Falhou ao processar",
};

const linkErrorLabel: Record<MedicoErrorCode, string> = {
	TRANSCRIPT_NOT_FOUND: "Esta transcrição não está mais na fila.",
	IDENTITY_UNRESOLVED: "Não foi possível resolver o paciente para este vínculo.",
	INVALID_GROUP: "O agrupamento aponta para outro paciente ou consulta não vinculada.",
	ANCHOR_NOT_FOUND: "Paciente não encontrado.",
	ANCHOR_NOT_ORPHAN: "Este paciente já está vinculado.",
	NOTHING_TO_EXTRACT: "Nada elegível para extrair: falta consentimento ou vínculo.",
	NOTHING_TO_RETRY: "Nenhum processamento disponível para nova tentativa.",
	SECTION_NOT_FOUND: "Seção do prontuário não encontrada.",
	LINK_NOT_LINKED: "Esta transcrição não está vinculada.",
	LINK_NOT_REVERTABLE:
		"Manual já entregue à paciente: esta consulta não pode mais voltar para a fila.",
	LINK_LAST_MEMBER:
		"Esta é a única gravação do grupo: use Devolver à fila para remover a consulta inteira.",
	JOB_NOT_FOUND: "Este job já saiu da lista de erros.",
};

const sectionLabel: Record<ProntuarioSection, string> = {
	complaint_expectations: "Queixa e expectativas",
	medical_history: "Antecedentes patológicos",
	surgeries_anesthesia: "Cirurgias e anestesias",
	allergies: "Alergias",
	medications: "Medicamentos",
	habits: "Hábitos",
	gyneco_obstetric: "Gineco-obstétrica",
	family_history: "História familiar",
	anthropometry: "Antropometria",
	physical_exam: "Exame físico",
	risk: "Risco",
	plan_conduct: "Plano e conduta",
};

const relanceLabel: Record<string, string> = {
	smoking: "Tabagismo e carga",
	tev_risk: "Risco de TEV",
	allergies: "Alergias e reações",
	bleeding_medication: "Anticoagulante/antiagregante/fitoterápico de sangramento",
	glp1: "Uso de GLP-1",
	isotretinoin: "Uso recente de isotretinoína",
	dm_has_apnea: "Riscos metabólicos e apneia",
	bmi_weight_bariatric: "Histórico ponderal e bariátrico",
	anesthetic_complication: "Complicação anestésica pessoal/familiar",
	asa: "Classificação ASA",
	mental_health: "Saúde mental e TDC",
	pregnancy_breastfeeding: "Gravidez e amamentação",
	keloid: "Queloide pessoal/familiar",
	breast_screening: "Rastreio mamário em dia",
};

const itemStateLabel: Record<"present" | "denied" | "absent", string> = {
	present: "presente",
	denied: "negado",
	absent: "não abordado",
};

const itemStateStyle = {
	present: { text: "text-info", surface: "border-info/30 bg-info-soft" },
	denied: { text: "text-primary", surface: "border-primary/30 bg-primary-soft" },
	absent: { text: "text-warning", surface: "border-warning/30 bg-warning-soft" },
};

const itemOriginLabel: Record<ItemOrigin, string> = {
	ia: "IA",
	dra: "Dra.",
	team: "equipe",
};

export const milestoneTypeLabel: Record<MilestoneType, string> = {
	consult: "Consulta",
	exam: "Exame",
	prescription: "Prescrição",
	surgery: "Cirurgia",
	return: "Retorno",
	intercurrence: "Intercorrência",
	consent: "Consentimento",
	manual: "Manual",
	measurement: "Medição",
	referral: "Encaminhamento",
};

const milestoneTitleLabel: Record<string, string> = {
	consent_recording: "Consentimento de gravação registrado",
};

const firefliesSealLabel = {
	liveLead: "Fireflies conectado",
	downLead: "Fireflies indisponível",
	downSub: "avulsas seguem funcionando",
	syncSubPrefix: "última sync",
	refresh: "Atualizar",
};

const firefliesSyncToastLabel: Record<string, string> = {
	unavailable: "Sincronização de gravações indisponível.",
	floor: "Nenhuma gravação nova.",
};

const redFlagLabel: Record<RedFlagRule, string> = {
	smoking_current: "Tabagismo ativo, risco de necrose cutânea",
	tev_personal_family: "Antecedente pessoal ou familiar de TEV",
	estrogen_plus_tev_factor: "Estrogênio associado a fator de risco de TEV",
	caprini_high: "Caprini alto",
	glp1_in_use: "GLP-1 em uso, risco de aspiração",
	bleeding_medication: "Medicação com risco de sangramento",
	isotretinoin: "Isotretinoína em uso ou recente",
	bdd_screening: "Sinais de TDC",
	mental_health_active: "Condição ativa de saúde mental",
	bariatric_recent_or_deficiency: "Bariátrica recente ou deficiência nutricional",
	bmi_30_plus: "IMC igual ou superior a 30",
	pregnancy_plans: "Planos de gravidez",
	breastfeeding_recent: "Amamentação recente",
	anesthetic_complication: "Complicação anestésica pessoal ou familiar",
	keloid: "Queloide pessoal ou familiar",
	dm_uncontrolled: "Diabetes sem controle",
	apnea_suspected: "Suspeita de apneia",
	breast_screening: "Rastreio mamário pendente",
	surgical_center_allergy: "Alergia relevante para o centro cirúrgico",
	critical_gaps: "Itens críticos não abordados na consulta",
};

type PipelineStage = MedicoPipelineItem["stage"];
type PatientStatus = MedicoPipelineItem["patientStatus"];

const pipelineStageLabel: Record<PipelineStage, string> = {
	queued: "na fila",
	awaiting_consent: "aguardando consentimento",
	extracting: "extraindo prontuário",
	awaiting_manual: "manual na fila",
	drafting: "gerando manual",
	prontuario_pending: "prontuário pendente",
	ready: "pronto",
	delivered: "entregue à paciente",
	revoked: "entregue e revogado",
	error: "com erro",
};

const pipelineStageTone: Record<PipelineStage, ChipTone> = {
	queued: "amber",
	awaiting_consent: "amber",
	extracting: "petrol",
	awaiting_manual: "amber",
	drafting: "petrol",
	prontuario_pending: "amber",
	ready: "teal",
	delivered: "teal",
	revoked: "coral",
	error: "coral",
};

const pipelinePatientStatusLabel: Record<Exclude<PatientStatus, "ok">, string> = {
	orphan: "paciente órfã",
	awaiting_registry: "aguardando cadastro civil",
};

const pipelinePatientStatusTone: Record<Exclude<PatientStatus, "ok">, ChipTone> = {
	orphan: "coral",
	awaiting_registry: "amber",
};

const pipelineErrorStepLabel: Record<NonNullable<MedicoPipelineItem["errorType"]>, string> = {
	extraction: "falhou na extração do prontuário",
	manual: "falhou na geração do manual",
};

export const MedicoLabels = {
	redFlag: redFlagLabel,
	transcriptClass: transcriptClassLabel,
	transcriptClassTone,
	origin: transcriptOriginLabel,
	originTone: transcriptOriginTone,
	jobType: jobTypeLabel,
	jobError(reason: MedJobErrorReason) {
		return jobErrorReasonLabel[reason];
	},
	section: sectionLabel,
	relance: relanceLabel,
	itemState: itemStateLabel,
	itemStateStyle,
	itemOrigin: itemOriginLabel,
	milestoneType: milestoneTypeLabel,
	milestoneTitle(code: string) {
		return milestoneTitleLabel[code] ?? code;
	},
	firefliesSeal: firefliesSealLabel,
	duration(durationS: number | null) {
		if (durationS === null) {
			return null;
		}

		return `${Math.max(1, Math.round(durationS / 60))} min`;
	},
	evidenceCount(count: number) {
		return `Evidência em ${count} sentença${count === 1 ? "" : "s"}`;
	},
	paste: {
		trigger: "Colar transcrição avulsa",
		title: "Colar transcrição avulsa",
		subtitle: "consulta online ou ditado",
		titlePlaceholder: "Título da transcrição",
		textPlaceholder: "Cole aqui a transcrição completa",
		submit: "Registrar transcrição",
		required: "título e texto são obrigatórios",
		error: "Não foi possível registrar a transcrição.",
	},
	transcriptFallbackTitle: "Transcrição",
	transcriptVoice(speaker: string) {
		const match = /^speaker (\d+)$/iu.exec(speaker);

		return match === null ? speaker : `Voz ${match[1]}`;
	},
	transcriptLoadError: "Não foi possível carregar a transcrição.",
	queueTitle: "Transcrições a vincular",
	jobErrorsTitle: "Processamentos interrompidos",
	jobErrorsSubtitle: "tentativas esgotadas",
	jobDiscard: "descartar",
	jobDiscardConfirm: "descartar mesmo?",
	jobDiscarding: "descartando...",
	jobRetry: "tentar novamente",
	jobRetrying: "tentando novamente...",
	jobAttempts(attempts: number) {
		return `${attempts} tentativa${attempts === 1 ? "" : "s"}`;
	},
	archivedManuals: {
		title: "Manuais arquivados",
		subtitle: "entregues e revogados, guardados como registro",
		consultationPrefix: "consulta",
		revokedPrefix: "revogado",
		unknownPatient: "paciente sem nome registrado",
	},
	queueEmpty: "fila limpa",
	queueEmptyTitle: "Nenhuma transcrição aguardando vínculo",
	suggestedClassPrefix: "sugere",
	transcriptRead: "ler transcrição",
	transcriptIgnore: "ignorar",
	transcriptIgnoring: "ignorando...",
	ignoreConfirm: "Ignorar esta transcrição?",
	ignoreConfirmAction: "confirmar",
	ignoreCancel: "cancelar",
	ignoredTitle: "Ignoradas",
	ignoredSubtitle(count: number) {
		return `${count} fora da fila de vincular`;
	},
	ignoredRestore: "devolver à fila",
	ignoredRestoring: "devolvendo...",
	suggestionTag: "sugestão do título",
	link: {
		title: "Vincular transcrição",
		read: "Ler transcrição",
		patientField: "Paciente",
		suggestionTag: "sugestão do título",
		classField: "Classificação",
		classHelp: "classificação sugerida",
		submit: "Vincular",
		linking: "vinculando transcrição...",
		needsTarget: "Paciente não selecionada",
		error: "Não foi possível vincular a transcrição.",
		leadNote: "Paciente sem cadastro civil completo.",
	},
	queuePending(count: number) {
		return `${count} aguardando dona, classificação e consentimento`;
	},
	groupSuggested(name: string) {
		return `Possível grupo com a consulta de ${name}`;
	},
	awaitingRegistryChip: pipelinePatientStatusLabel.awaiting_registry,
	pipeline: {
		title: "Consultas vinculadas",
		subtitle: "estado do prontuário e do manual",
		stageTone: pipelineStageTone,
		patientStatus: pipelinePatientStatusLabel,
		patientStatusTone: pipelinePatientStatusTone,
		stageLabel(stage: PipelineStage, errorType: MedicoPipelineItem["errorType"] | undefined) {
			if (stage === "error" && errorType) {
				return pipelineErrorStepLabel[errorType];
			}

			return pipelineStageLabel[stage];
		},
		emptyTitle: "Nenhuma consulta vinculada em andamento",
		step(step: number, total: number) {
			return `etapa ${step} de ${total}`;
		},
		recordings(count: number) {
			return `${count} ${count === 1 ? "gravação" : "gravações"}`;
		},
		subprogress(done: number, total: number) {
			return `${done}/${total}`;
		},
		openManual: "abrir manual",
		openProntuario: "abrir prontuário",
		manualReady: "manual pronto",
		elapsed(minutes: number) {
			if (minutes < 1) {
				return "nesta etapa agora há pouco";
			}

			if (minutes < 60) {
				return `nesta etapa há ${minutes} min`;
			}

			if (minutes < 1440) {
				return `nesta etapa há ${Math.floor(minutes / 60)} h`;
			}

			return `nesta etapa há ${Math.floor(minutes / 1440)} d ${Math.floor((minutes % 1440) / 60)} h`;
		},
		unlink: "Devolver à fila",
		unlinking: "devolvendo...",
		unlinkConfirm(opts: { partsCount: number; revoked: boolean }) {
			if (opts.revoked) {
				if (opts.partsCount > 1) {
					return `Devolver as ${opts.partsCount} gravações deste grupo para a fila de vincular? O manual entregue e revogado fica guardado como registro; a extração do grupo é descartada.`;
				}

				return "Devolver esta consulta para a fila de vincular? O manual entregue e revogado fica guardado como registro; a extração desta consulta é descartada.";
			}

			if (opts.partsCount > 1) {
				return `Devolver as ${opts.partsCount} gravações deste grupo para a fila de vincular? O rascunho do manual e a extração do grupo são descartados.`;
			}

			return "Devolver esta consulta para a fila de vincular? O rascunho do manual e a extração desta consulta são descartados.";
		},
		confirm: "confirmar",
		cancel: "cancelar",
		partsTitle: "Gravações do grupo",
		removePart: "tirar esta gravação",
		removingPart: "tirando...",
		removePartConfirm:
			"Tirar só esta gravação e devolvê-la à fila? O grupo continua; o prontuário e o manual são refeitos com as demais gravações.",
	},
	linkedTranscripts: {
		title: "Transcrições vinculadas",
		empty: "Nenhuma transcrição vinculada ainda.",
	},
	reextract: {
		action: "Re-extrair",
		confirm: "Confirmar",
		pending: "re-extraindo...",
		success(queued: number) {
			return `${queued} grupo${queued === 1 ? "" : "s"} re-enfileirado${queued === 1 ? "" : "s"}`;
		},
	},
	gate: {
		title: "Prontuários sem vínculo civil",
		subtitle: "vínculo civil pendente",
		tag: {
			orphan: "vínculo perdido",
			awaiting: "fora do cadastro",
		},
		action: "Vincular",
		modalTitle: "Vincular ao cadastro",
		explanation: {
			orphan: "O vínculo entre o prontuário e o cadastro foi perdido.",
			awaiting: "Paciente clínica fora do cadastro civil.",
		},
		relinkLeadHint: "Lead indisponível para vínculo civil.",
	},
	syncToast(result: { live: boolean; new: number }) {
		if (!result.live) {
			return firefliesSyncToastLabel.unavailable;
		}

		if (result.new === 0) {
			return firefliesSyncToastLabel.floor;
		}

		const plural = result.new === 1 ? "" : "s";

		return `${result.new} transcrição${plural} nova${plural} do Fireflies.`;
	},
	freshness(live: boolean) {
		return live ? "ao vivo" : "réplica";
	},
	error(code: string | undefined) {
		return actionError(linkErrorLabel, code);
	},
};
